// Admin > Workflow. Pick a merchant, then use that store's Components / Library / Workflow
// (Menu Data Extraction, Image Extraction, Image Creation) exactly as the merchant sees them.
//
// The workflow screens work on "the active store" (localStorage customer_id) and authorise an admin
// through Merchant Mode (see utils/adminMerchantMode.js), so choosing a merchant here puts the browser
// into the same state as "Login as Merchant" - without leaving the Admin panel - and leaving this page
// puts back whatever was there before, so the rest of the Admin panel is not left impersonating anyone.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Select } from "antd";
import axios from "axios";
import API_BASE from "../config/api";
import WorkflowSection from "../components/workflow/WorkflowSection";
import { ACTIVE_CUSTOMER_ID_CHANGED_EVENT } from "../hooks/useMerchantStores";
import { enterAdminMerchantMode, exitAdminMerchantMode, isAdminMerchantMode } from "../utils/adminMerchantMode";

const putItem = (key, value) => (value === null || value === undefined ? localStorage.removeItem(key) : localStorage.setItem(key, value));

const AdminWorkflow = () => {
  const [merchantList, setMerchantList] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [loadError, setLoadError] = useState("");

  // What the browser held when this page opened (an admin may already be inside "Login as Merchant").
  const before = useRef(null);
  if (before.current === null) {
    before.current = {
      modeActive: isAdminMerchantMode(),
      customerId: localStorage.getItem("customer_id"),
      phone: localStorage.getItem("phone"),
    };
  }

  const restoreSession = useCallback(() => {
    const prev = before.current;
    if (!prev.modeActive) exitAdminMerchantMode(); // clears the merchant-mode flag, customer_id and phone
    putItem("customer_id", prev.customerId);
    putItem("phone", prev.phone);
  }, []);

  // Leaving the page (or closing/reloading the tab) must not leave the admin impersonating a merchant.
  useEffect(() => {
    window.addEventListener("pagehide", restoreSession);
    return () => {
      window.removeEventListener("pagehide", restoreSession);
      restoreSession();
    };
  }, [restoreSession]);

  useEffect(() => {
    axios
      .get(`${API_BASE}/fetchMerchantForMode`)
      .then(({ data }) => setMerchantList(data?.data || []))
      .catch((err) => {
        console.error("Workflow: couldn't load merchants", err);
        setLoadError("Couldn't load the merchant list. Please refresh the page.");
      });
  }, []);

  const merchants = useMemo(() => new Map(merchantList.filter((m) => m.customer_id).map((m) => [String(m.customer_id), m])), [merchantList]);

  const options = useMemo(
    () =>
      Array.from(merchants.values()).map((m) => ({
        label: m.full_name || m.name || "Unnamed",
        value: String(m.customer_id),
      })),
    [merchants]
  );

  const handleSelect = (value) => {
    if (!value) {
      restoreSession();
      setCustomerId("");
      return;
    }
    const merchant = merchants.get(String(value));
    localStorage.setItem("customer_id", String(value));
    localStorage.setItem("phone", merchant?.mobile || "");
    enterAdminMerchantMode();
    setCustomerId(String(value));
    window.dispatchEvent(new CustomEvent(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, { detail: { customerId: String(value) } }));
  };

  const selected = customerId ? merchants.get(customerId) : null;

  return (
    <div className="modSpace">
      <h2>Workflow</h2>
      <p style={{ color: "#6b7280", marginBottom: 12 }}>
        Choose a merchant to use their Components, Library and Workflow. Everything you do here is saved to that merchant&apos;s store.
      </p>

      <Select
        showSearch
        allowClear
        virtual={false}
        style={{ width: 320, marginBottom: 16 }}
        placeholder="Select a merchant"
        value={customerId || undefined}
        onChange={handleSelect}
        filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
        options={options}
      />

      {loadError && <p style={{ color: "#b91c1c" }}>{loadError}</p>}

      {!customerId ? (
        <div className="rounded-2xl border-2 border-dashed border-gray-200 px-4 py-10 text-center text-sm text-gray-500">
          Select a merchant above to open their workflow.
        </div>
      ) : (
        <div className="w-full max-w-6xl">
          <p className="mb-3 rounded-lg bg-indigo-50 px-3 py-2 text-sm text-indigo-800">
            Working on <strong>{selected?.full_name || selected?.name || "this merchant"}</strong>
          </p>
          <WorkflowSection key={customerId} />
        </div>
      )}
    </div>
  );
};

export default AdminWorkflow;
